'use client';

import { useContext } from 'react';

import { AppConfigContext } from './app-config.context';

import type { AppConfig } from '@utazzo/shared';

/**
 * Access the runtime AppConfig.
 *
 * Throws if used outside <AppConfigProvider>. This is intentional - if a
 * component renders before the config has loaded, something is wrong with
 * the boot sequence and we want it to be loud.
 */
export const useAppConfig = (): AppConfig => {
  const ctx = useContext(AppConfigContext);
  if (!ctx) {
    throw new Error('useAppConfig must be used inside <AppConfigProvider>');
  }
  return ctx.config;
};

/**
 * Convenience selectors. Components should prefer these over reading the full
 * config tree, both for clarity and to make refactoring easier.
 */

export const useBranding = () => useAppConfig().branding;
export const useLocalization = () => useAppConfig().localization;
export const useLimits = () => useAppConfig().limits;

/**
 * Returns the boolean state of a named feature flag. Unknown flags return
 * false (closed by default), so accidental references don't accidentally
 * enable functionality.
 */
export const useFeatureFlag = (key: string): boolean => {
  const { features } = useAppConfig();
  return features[key] ?? false;
};
